import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  ViewChild,
  afterNextRender,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';

import { GameState } from '@core/tellent-skills-types';

import {
  FRAME_DELAY,
  GAME_SPEED_MAX,
  GAME_SPEED_START,
  GROUND_COLOR,
  LETTER_DEFS,
  MIN_OBJECT_GAP,
} from './constants';
import { Cloud, GrassTuft, Lion, Obstacle, Star, checkCollision } from './entities';

@Component({
  selector: 'rt-loading-game-frame',
  host: { '(window:keydown)': 'onKeyDown($event)' },
  templateUrl: './loading-game-frame.component.html',
  styleUrl: './loading-game-frame.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoadingGameFrameComponent {
  @ViewChild('gameCanvas') private readonly canvasRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('gameContainer') private readonly containerRef!: ElementRef<HTMLDivElement>;

  public readonly startText = input.required<string>();
  public readonly tutorialText = input.required<string>();
  public readonly gameOverText = input.required<string>();
  public readonly scoreText = input.required<string>();

  public readonly gameState = signal<GameState>('idle');
  public readonly lives = signal(3);
  public readonly tutorialVisible = signal(false);
  public readonly displayScore = signal('00000');
  public readonly hearts = computed(() => Array(this.lives()).fill(null));

  private canvas!: HTMLCanvasElement;
  private ctx!: CanvasRenderingContext2D;
  private animationId?: number;
  private tutorialTimeoutId?: ReturnType<typeof setTimeout>;
  private resizeObserver?: ResizeObserver;

  private gameSpeed = GAME_SPEED_START;
  private rawScore = 0;
  private frameCount = 0;
  private groundOffset = 0;
  private currentLetterIndex = 0;
  private groundY = 0;
  private lion: Lion | null = null;
  private lastObjectRight = 0;
  private lastFrameTime = 0;
  private obstacles: Obstacle[] = [];
  private stars: Star[] = [];
  private clouds: Cloud[] = [];
  private grassTufts: GrassTuft[] = [];

  constructor() {
    afterNextRender(() => {
      this.canvas = this.canvasRef.nativeElement;
      this.ctx = this.canvas.getContext('2d')!;
      this.canvas.addEventListener('touchstart', this.onCanvasTouchStart, { passive: false });
      this.setupResizeObserver();
      this.resetGame();
      this.animationId = requestAnimationFrame(this.animateLoop);
    });

    inject(DestroyRef).onDestroy(() => {
      if (this.animationId !== undefined) cancelAnimationFrame(this.animationId);
      if (this.tutorialTimeoutId !== undefined) clearTimeout(this.tutorialTimeoutId);
      this.canvas?.removeEventListener('touchstart', this.onCanvasTouchStart);
      this.resizeObserver?.disconnect();
    });
  }

  protected onKeyDown(event: KeyboardEvent): void {
    if (event.code === 'Space' || event.code === 'ArrowUp') {
      event.preventDefault();
      this.handleInput();
    }
  }

  protected handleInput(): void {
    const state = this.gameState();
    if (state === 'idle') {
      this.gameState.set('playing');
      this.triggerTutorial();
      this.lion?.jump();
    } else if (state === 'over') {
      this.resetGame();
      this.gameState.set('playing');
      this.triggerTutorial();
      this.lion?.jump();
    } else {
      this.lion?.jump();
    }
  }

  private readonly onCanvasTouchStart = (event: TouchEvent): void => {
    event.preventDefault();
    this.handleInput();
  };

  private setupResizeObserver(): void {
    this.resizeObserver = new ResizeObserver(() => this.resizeCanvas());
    this.resizeObserver.observe(this.containerRef.nativeElement);
  }

  private resizeCanvas(): void {
    const container = this.containerRef.nativeElement;
    this.canvas.width = container.clientWidth;
    this.canvas.height = container.clientHeight;
    this.groundY = this.canvas.height - 5;

    if (this.lion) {
      this.lion.x = this.canvas.width / 2 - this.lion.width / 2;
      if (!this.lion.isJumping && this.gameState() !== 'playing') {
        this.lion.y = this.groundY - this.lion.height;
      }
    }

    if (this.clouds.length === 0 && this.canvas.width > 0) {
      this.clouds.push(
        new Cloud(this.canvas.width * 0.1, this.groundY),
        new Cloud(this.canvas.width * 0.4, this.groundY),
        new Cloud(this.canvas.width * 0.7, this.groundY),
        new Cloud(this.canvas.width * 1.1, this.groundY),
      );
    }

    if (this.grassTufts.length === 0 && this.canvas.width > 0) {
      this.grassTufts.push(
        new GrassTuft(this.canvas.width * 0.2, this.groundY),
        new GrassTuft(this.canvas.width * 0.6, this.groundY),
        new GrassTuft(this.canvas.width * 0.9, this.groundY),
      );
    }
  }

  private resetGame(): void {
    this.lion = new Lion();
    this.obstacles = [];
    this.stars = [];
    this.grassTufts = [];
    this.gameSpeed = GAME_SPEED_START;
    this.rawScore = 0;
    this.displayScore.set('00000');
    this.lives.set(3);
    this.frameCount = 0;
    this.currentLetterIndex = 0;
    this.lastFrameTime = performance.now();
    this.gameState.set('idle');
    this.resizeCanvas();
    this.lastObjectRight = this.canvas.width / 2;
  }

  private triggerTutorial(): void {
    this.tutorialVisible.set(true);
    if (this.tutorialTimeoutId !== undefined) clearTimeout(this.tutorialTimeoutId);
    this.tutorialTimeoutId = setTimeout(() => this.tutorialVisible.set(false), 2000);
  }

  private readonly animateLoop = (timestamp: number): void => {
    this.animationId = requestAnimationFrame(this.animateLoop);

    const elapsed = timestamp - this.lastFrameTime;
    if (elapsed <= FRAME_DELAY) return;
    this.lastFrameTime = timestamp - (elapsed % FRAME_DELAY);

    const { ctx, canvas } = this;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const isPlaying = this.gameState() === 'playing';

    if (isPlaying) {
      this.rawScore += 0.05;
      if (this.gameSpeed < GAME_SPEED_MAX) this.gameSpeed += 0.002;
      this.frameCount++;
      if (this.frameCount % 10 === 0) {
        this.displayScore.set(Math.floor(this.rawScore).toString().padStart(5, '0'));
      }
    }

    this.drawClouds(isPlaying);
    this.drawGrassTufts(isPlaying);
    this.drawGround(isPlaying);

    if (isPlaying && this.lion) {
      this.lion.update(this.groundY);
      this.spawnObjects();
    }

    if (this.lion) this.lion.draw(ctx, this.frameCount, isPlaying);

    for (let i = this.stars.length - 1; i >= 0; i--) {
      const star = this.stars[i];
      if (isPlaying) star.update(this.gameSpeed);
      star.draw(ctx);
      if (
        isPlaying &&
        !star.gathered &&
        this.lion &&
        checkCollision(this.lion.getHitbox(), star.getHitbox())
      ) {
        star.gathered = true;
        this.rawScore += 10;
        this.displayScore.set(Math.floor(this.rawScore).toString().padStart(5, '0'));
      }
      if (star.getRight() < 0) this.stars.splice(i, 1);
    }

    for (let i = this.obstacles.length - 1; i >= 0; i--) {
      const obs = this.obstacles[i];
      if (isPlaying || obs.isDestroyed) obs.update(this.gameSpeed);
      if (
        isPlaying &&
        !obs.isDestroyed &&
        this.lion &&
        checkCollision(this.lion.getHitbox(), obs.getHitbox())
      ) {
        obs.destroy();
        const newLives = this.lives() - 1;
        this.lives.set(newLives);
        if (newLives <= 0) {
          this.gameState.set('over');
          this.displayScore.set(Math.floor(this.rawScore).toString().padStart(5, '0'));
        }
      }
      obs.draw(ctx);
      if (obs.y > canvas.height + 50 || obs.getRight() < -50) this.obstacles.splice(i, 1);
    }
  };

  private spawnObjects(): void {
    this.lastObjectRight -= this.gameSpeed;
    const spawnRate = Math.max(40, 90 - this.gameSpeed * 2);
    if (this.frameCount % Math.floor(spawnRate) !== 0) return;

    const safetyZone = this.canvas.width + MIN_OBJECT_GAP;
    if (this.lastObjectRight >= safetyZone) return;

    if (Math.random() > 0.4 && this.obstacles.length > 0) {
      const lastRight = this.obstacles[this.obstacles.length - 1].getRight();
      if (this.canvas.width > lastRight + MIN_OBJECT_GAP) {
        const star = new Star(this.canvas.width, this.groundY);
        this.stars.push(star);
        this.lastObjectRight = star.getRight();
      }
    } else if (Math.random() > 0.3) {
      const obs = new Obstacle(this.canvas.width, this.groundY, this.currentLetterIndex);
      this.currentLetterIndex = (this.currentLetterIndex + 1) % LETTER_DEFS.length;
      this.obstacles.push(obs);
      this.lastObjectRight = obs.getRight();
    }
  }

  private drawClouds(isPlaying: boolean): void {
    const { ctx, canvas } = this;
    if (this.clouds.length < 5) {
      const maxX = this.clouds.reduce((m, c) => Math.max(m, c.getRight()), 0);
      if (canvas.width > maxX - 50) {
        this.clouds.push(new Cloud(canvas.width + Math.random() * 80, this.groundY));
      }
    }
    for (let i = this.clouds.length - 1; i >= 0; i--) {
      this.clouds[i].update(isPlaying, this.gameSpeed);
      this.clouds[i].draw(ctx);
      if (this.clouds[i].getRight() < -10) this.clouds.splice(i, 1);
    }
  }

  private drawGrassTufts(isPlaying: boolean): void {
    const { ctx, canvas } = this;
    if (isPlaying && this.grassTufts.length < canvas.width / 50) {
      const maxX = this.grassTufts.reduce((m, g) => Math.max(m, g.getRight()), 0);
      if (canvas.width > maxX + Math.random() * 150) {
        this.grassTufts.push(new GrassTuft(canvas.width + Math.random() * 50, this.groundY));
      }
    }
    for (let i = this.grassTufts.length - 1; i >= 0; i--) {
      if (isPlaying) this.grassTufts[i].update(this.gameSpeed);
      this.grassTufts[i].draw(ctx);
      if (this.grassTufts[i].getRight() < -10) this.grassTufts.splice(i, 1);
    }
  }

  private drawGround(isPlaying: boolean): void {
    const { ctx, canvas } = this;
    ctx.beginPath();
    ctx.moveTo(0, this.groundY);
    ctx.lineTo(canvas.width, this.groundY);
    ctx.strokeStyle = GROUND_COLOR;
    ctx.lineWidth = 2;
    ctx.stroke();

    if (isPlaying) {
      this.groundOffset -= this.gameSpeed;
      if (this.groundOffset < -40) this.groundOffset = 0;
    }

    ctx.fillStyle = GROUND_COLOR;
    for (let i = this.groundOffset; i < canvas.width; i += 40) {
      if (i > 0) ctx.fillRect(i, this.groundY + 5, 3, 1);
    }
  }
}
